'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

type SearchParam<T extends Record<string, string>> = {
  [K in keyof T]: {
    key: K;
    value: T[K];
  };
}[keyof T];

export const useNavigation = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const getSearchParams = <T extends Record<string, string>>(): T => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    const queryParams = {} as T;

    for (const [key, value] of params) {
      queryParams[key as keyof T] = value as T[keyof T];
    }

    return queryParams;
  };

  const addSearchParam = <T extends Record<string, string>>(data: SearchParam<T> | SearchParam<T>[]) => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));

    const setParam = (item: SearchParam<T>) => {
      params.set(item.key as string, item.value);
    };

    if (Array.isArray(data)) {
      data.forEach(setParam);
    } else {
      setParam(data);
    }

    const search = params.toString();
    const query = search ? `?${search}` : '';
    router.push(`${pathname}${query}`, { scroll: false });
  };

  const removeSearchParam = (data: string | string[]) => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    Array.isArray(data)
      ? data.forEach((item) => {
        params.delete(item);
      })
      : params.delete(data);

    const search = params.toString();
    const query = search ? `?${search}` : '';
    router.push(`${pathname}${query}`, { scroll: false });
  };

  const removeAllSearchParams = () => {
    if (!searchParams.toString()) return;

    router.replace(`${pathname}#`, { scroll: false });
  };

  return {
    getSearchParams,
    addSearchParam,
    removeSearchParam,
    removeAllSearchParams
  };
};
